import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';

import { readableCurveError } from './errors';
import { guided } from '../../app/guidance/fieldHelpRegistryCore';
import { curveImportFieldHelp } from '../../app/guidance/fieldHelpRegistry';
import type { CurveLibraryApi } from './libraryApi';
import type { CurveLibraryDetail, CurveLibraryEntry, CurveLibraryForkResult, CurveLibraryPage, CurveLibrarySearch } from './libraryTypes';
import type { CurveImportRead } from './types';

interface ReviewedCurveLibraryProps {
  api: CurveLibraryApi;
  authenticated: boolean;
  canReview: boolean;
  disabled?: boolean;
  imported?: CurveImportRead;
  onFork: (result: CurveLibraryForkResult) => void;
  onOpenImport: (id: string) => void;
  onContinueAnalysis?: () => void;
  onBusyChange?: (busy: boolean) => void;
}

function nextPage(page: { offset: number; limit: number; total: number }) {
  const next = page.offset + page.limit;
  return next < page.total ? next : undefined;
}

function entryLabel(entry: CurveLibraryEntry) {
  return `${entry.manufacturer} ${entry.model}${entry.trim_label === null ? '' : ` · ${entry.trim_label}`}`;
}

function reviewedFleetUrl(detail: CurveLibraryDetail, target: CurveLibraryDetail['catalog_targets'][number]) {
  const params = new URLSearchParams({
    library_entry_id: detail.entry.id,
    pump_template_id: target.pump_template_id,
    pump_template_revision_id: target.pump_template_revision_id,
    pump_variant_id: target.pump_variant_id,
  });
  return `/fleet?${params.toString()}`;
}

export function ReviewedCurveLibrary({
  api, authenticated, canReview, disabled = false, imported,
  onFork, onOpenImport, onContinueAnalysis, onBusyChange,
}: ReviewedCurveLibraryProps) {
  const queryClient = useQueryClient();
  const initialManufacturer = imported?.suggestions.manufacturer ?? '';
  const initialModel = imported?.suggestions.model ?? '';
  const [manufacturer, setManufacturer] = useState(initialManufacturer);
  const [model, setModel] = useState(initialModel);
  const [trimLabel, setTrimLabel] = useState('');
  const [identity, setIdentity] = useState<CurveLibrarySearch | null>(
    initialManufacturer.trim() && initialModel.trim()
      ? { manufacturer: initialManufacturer, model: initialModel } : null,
  );
  const [detail, setDetail] = useState<CurveLibraryDetail | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewAttested, setReviewAttested] = useState(false);
  const operation = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; onBusyChange?.(false); };
  }, [onBusyChange]);

  const sourceHash = imported?.source.sha256;
  const sourceResults = useInfiniteQuery({
    enabled: authenticated && sourceHash !== undefined,
    queryKey: ['curve-library', 'source', canReview, sourceHash],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => api.search({ source_sha256: sourceHash ?? '' }, pageParam),
    getNextPageParam: nextPage,
    retry: false,
  });
  const identityResults = useInfiniteQuery({
    enabled: authenticated && identity !== null,
    queryKey: ['curve-library', 'identity', canReview, identity],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => api.search(identity ?? {}, pageParam),
    getNextPageParam: nextPage,
    retry: false,
  });
  const queue = useInfiniteQuery({
    enabled: authenticated && canReview && queueOpen,
    queryKey: ['curve-library', 'review-queue'],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => api.reviewQueue(pageParam),
    getNextPageParam: nextPage,
    retry: false,
  });
  const forks = useInfiniteQuery({
    enabled: authenticated && canReview && queueOpen,
    queryKey: ['curve-library', 'fork-queue'],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => api.forkQueue(pageParam),
    getNextPageParam: nextPage,
    retry: false,
  });
  const allEntries = [...(sourceResults.data?.pages ?? []), ...(identityResults.data?.pages ?? [])]
    .flatMap((page) => page.items).filter((entry) => entry.review_state === 'approved');
  const entries = [...new Map(allEntries.map((entry) => [entry.id, entry])).values()];
  const loading = sourceResults.isFetching || identityResults.isFetching;
  const lookupFailed = sourceResults.isError || identityResults.isError;
  const searched = sourceHash !== undefined || identity !== null;

  const search = (event: FormEvent) => {
    event.preventDefault();
    if (!manufacturer.trim() || !model.trim() || disabled || busy) return;
    setIdentity({ manufacturer: manufacturer.trim(), model: model.trim(), ...(trimLabel.trim() ? { trim_label: trimLabel.trim() } : {}) });
    setDetail(null);
    setError('');
  };

  const act = async (action: () => Promise<void>) => {
    if (operation.current || disabled || !authenticated) return;
    operation.current = true;
    setBusy(true);
    setError('');
    onBusyChange?.(true);
    try { await action(); }
    catch (cause: unknown) { if (alive.current) setError(readableCurveError(cause)); }
    finally {
      operation.current = false;
      if (alive.current) { setBusy(false); onBusyChange?.(false); }
    }
  };
  const open = (id: string) => act(async () => {
    const selected = await api.getEntry(id);
    if (!alive.current) return;
    setDetail(selected);
    setReason('');
    setReviewNotes('');
    setReviewAttested(false);
    setNotice('');
  });
  const fork = () => act(async () => {
    if (detail === null || detail.entry.review_state !== 'approved' || reason.trim().length < 3) return;
    const result = await api.fork(detail.entry.id, reason.trim(), imported?.id ?? null);
    if (alive.current) onFork(result);
  });
  const review = (decision: 'approved' | 'rejected' | 'withdrawn') => act(async () => {
    if (!canReview || detail === null || reviewNotes.trim().length < 3 || (decision === 'approved' && !reviewAttested)) return;
    const result = await api.review(detail.entry.id, decision, reviewNotes.trim());
    if (!alive.current) return;
    setDetail(result);
    setReviewAttested(false);
    setNotice(`Library entry ${decision}. Existing source and revision history were retained.`);
    await queryClient.invalidateQueries({ queryKey: ['curve-library'] });
  });
  const renderEntries = (items: CurveLibraryEntry[], queueList = false) => (
    <ul className="curve-recent-list" aria-label={queueList ? 'Library approval submissions' : 'Reviewed curve matches'}>
      {items.map((entry) => <li key={entry.id}>
        <div>
          <strong>{entryLabel(entry)}</strong>
          <span>{entry.tested_speeds_rpm.join(', ')} RPM · {entry.revision_ids.length} saved speed revisions</span>
          <small>{entry.source_filename} · {entry.review_state}</small>
          {!canReview || entry.reviewed_by === null ? null : <small>Reviewed by {entry.reviewed_by}</small>}
        </div>
        <button disabled={disabled || busy} type="button" onClick={() => void open(entry.id)}>
          {queueList ? 'Review submission' : 'View reviewed curves'}
        </button>
      </li>)}
    </ul>
  );
  const targets = detail === null ? [] : [...new Map(detail.catalog_targets
    .filter((target) => target.active && detail.entry.revision_ids.length > 0 && detail.entry.revision_ids.every((revisionId) =>
      detail.catalog_targets.some((member) => member.active && member.curve_revision_id === revisionId &&
        member.pump_template_id === target.pump_template_id &&
        member.pump_template_revision_id === target.pump_template_revision_id &&
        member.pump_variant_id === target.pump_variant_id)))
    .map((target) => [`${target.pump_template_id}:${target.pump_template_revision_id}:${target.pump_variant_id}`, target])).values()];

  if (!authenticated) return <section aria-label="Private reviewed curve library">
    <h2>Private reviewed curve library</h2>
    <p>Sign in to search the internal reviewed library. It is not a public database.</p>
    {onContinueAnalysis === undefined ? null : <button disabled={disabled} type="button" onClick={onContinueAnalysis}>Continue with new analysis</button>}
  </section>;

  return <section className="curve-simple-card curve-reviewed-library" aria-label="Private reviewed curve library">
    <h2>Check the reviewed curve library first</h2>
    <p>Internal, authenticated workspace library. Match the pump, impeller, speeds, and source before reusing a reviewed configuration. A saved curve is not automatically an approved master.</p>
    <form onSubmit={search}>
      <div className="curve-form-grid">
        <label>Manufacturer<input {...guided(curveImportFieldHelp.libraryManufacturer, 'lookup')} aria-label="Library manufacturer" maxLength={200} value={manufacturer} disabled={disabled || busy} onChange={(event) => setManufacturer(event.target.value)} /></label>
        <label>Pump model<input {...guided(curveImportFieldHelp.libraryModel, 'lookup')} aria-label="Library pump model" maxLength={200} value={model} disabled={disabled || busy} onChange={(event) => setModel(event.target.value)} /></label>
        <label>Trim (optional)<input {...guided(curveImportFieldHelp.libraryTrim, 'lookup')} aria-label="Library trim" maxLength={200} value={trimLabel} disabled={disabled || busy} onChange={(event) => setTrimLabel(event.target.value)} /></label>
      </div>
      <button disabled={disabled || busy || !manufacturer.trim() || !model.trim()} type="submit">Search reviewed pumps</button>
    </form>
    {loading ? <p role="status">Checking reviewed sources and pump identity…</p> : null}
    {lookupFailed ? <div role="alert" className="curve-alert">
      <p>The private library check could not be completed. No AI request was sent.</p>
      <button disabled={disabled || busy || loading} type="button" onClick={() => {
        if (sourceHash !== undefined) void sourceResults.refetch();
        if (identity !== null) void identityResults.refetch();
      }}>Retry library check</button>
    </div> : null}
    {searched && !loading && !lookupFailed && entries.length === 0 ? <p>No approved configuration matched. Check the identity or continue with a new analysis.</p> : null}
    {renderEntries(entries)}
    {sourceResults.hasNextPage ? <button disabled={loading || disabled} type="button" onClick={() => void sourceResults.fetchNextPage()}>More exact-source matches</button> : null}
    {identityResults.hasNextPage ? <button disabled={loading || disabled} type="button" onClick={() => void identityResults.fetchNextPage()}>More pump matches</button> : null}
    {onContinueAnalysis === undefined ? null : <button disabled={disabled || busy || loading} type="button" onClick={onContinueAnalysis}>Continue with new analysis</button>}
    {notice ? <p role="status">{notice}</p> : null}
    {error ? <p role="alert" className="curve-alert">{error}</p> : null}

    {detail === null ? null : <section aria-label="Reviewed curve details">
      <h3>{entryLabel(detail.entry)} · {detail.entry.review_state}</h3>
      <p>Immutable curve bundle: {detail.entry.tested_speeds_rpm.join(', ')} RPM. Impeller: {detail.entry.impeller_diameter_m === null ? 'not recorded' : `${detail.entry.impeller_diameter_m} m`}.</p>
      <a href={api.sourceUrl(detail.entry.id)} target="_blank" rel="noreferrer">Open private source document</a>
      <p>Source fingerprint: <code>{detail.entry.source_sha256}</code></p>
      {detail.entry.parent_library_entry_id === null ? null : <p>Separate copy of library entry <code>{detail.entry.parent_library_entry_id}</code>.</p>}
      {detail.entry.compatibility_notes.map((note, index) => <p className="curve-alert" key={index}>{note}</p>)}
      {detail.revisions.map((revision) => <details key={revision.id}>
        <summary>{revision.tested_speed_rpm} RPM · {revision.curve_set_name} · {revision.revision_label}</summary>
        <p>Revision fingerprint: <code>{revision.content_sha256}</code>.{canReview ? ` Saved by ${revision.reviewer}.` : ''}</p>
        {revision.measured_traces.map((trace) => <details key={trace.id}>
          <summary>{trace.label} · {trace.origin.replaceAll('_', ' ')} · {trace.points.length} points</summary>
          <table aria-label={`${revision.tested_speed_rpm} RPM ${trace.label} immutable points`}>
            <thead><tr><th>Flow ({trace.x_source_unit})</th><th>{trace.y_quantity} ({trace.y_source_unit})</th></tr></thead>
            <tbody>{trace.points.map((point, index) => <tr key={index}><td>{point.source_x}</td><td>{point.source_y}</td></tr>)}</tbody>
          </table>
        </details>)}
        {revision.affinity_derived_traces.length === 0 ? null : <p>{revision.affinity_derived_traces.length} affinity-generated traces remain identified as predictions, not tested data.</p>}
        {revision.findings.map((finding, index) => <p key={index}>{finding.message}</p>)}
      </details>)}
      {detail.entry.review_state === 'approved' ? <>
        {targets.length === 0 ? <p>No active reviewed equipment link is available. Inspection does not make this curve eligible for fleet planning.</p> : targets.map((target) => <p key={target.pump_variant_id}>
          <Link aria-disabled={disabled || busy} onClick={(event) => { if (disabled || busy) event.preventDefault(); }} to={reviewedFleetUrl(detail, target)}>Use reviewed curve in fleet</Link>
        </p>)}
        <p>Using the reviewed curve opens equipment setup; you still supply the physical unit and driver and explicitly save it.</p>
        <label>Why create a modified copy?<textarea {...guided(curveImportFieldHelp.libraryModificationReason)} aria-label="Modified copy reason" disabled={disabled || busy} maxLength={2000} value={reason} onChange={(event) => setReason(event.target.value)} /></label>
        <p>The reviewed master stays unchanged. The separate copy is unreviewed until checked and approved independently. Changing an impeller label does not rescale performance; review the actual curve values and pump configuration.</p>
        <button disabled={disabled || busy || reason.trim().length < 3} type="button" onClick={() => void fork()}>Create separate modified copy</button>
      </> : null}
      {canReview ? <section aria-label="Curator decision">
        <label>Curator review notes<textarea {...guided(curveImportFieldHelp.libraryReviewNotes)} aria-label="Curator review notes" maxLength={4000} disabled={disabled || busy} value={reviewNotes} onChange={(event) => setReviewNotes(event.target.value)} /></label>
        {detail.entry.review_state === 'pending' ? <>
          <label><input {...guided(curveImportFieldHelp.libraryReviewAttestation)} type="checkbox" checked={reviewAttested} disabled={disabled || busy} onChange={(event) => setReviewAttested(event.target.checked)} />I checked every included speed, source curve, calibration, units, impeller, and compatibility note.</label>
          <button disabled={disabled || busy || !reviewAttested || reviewNotes.trim().length < 3} type="button" onClick={() => void review('approved')}>Approve reviewed master</button>
          <button disabled={disabled || busy || reviewNotes.trim().length < 3} type="button" onClick={() => void review('rejected')}>Reject submission</button>
        </> : null}
        {detail.entry.review_state === 'approved' ? <button disabled={disabled || busy || reviewNotes.trim().length < 3} type="button" onClick={() => void review('withdrawn')}>Withdraw master from reuse</button> : null}
      </section> : null}
      {canReview ? <details><summary>Library review history</summary><ul>{detail.review_events.map((event) => <li key={event.id}>{event.decision} by {event.actor} · {new Date(event.occurred_at).toLocaleString()} · {event.notes}</li>)}</ul></details> : null}
    </section>}

    {canReview ? <details onToggle={(event) => setQueueOpen(event.currentTarget.open)}>
      <summary>Curator queues</summary>
      <p>Approval is restricted to authenticated administrators or the local workspace curator, separately from saving a user's checked curve.</p>
      {queue.isFetching || forks.isFetching ? <p>Loading curator queues…</p> : null}
      {queue.isError || forks.isError ? <p role="alert">Curator queue unavailable. <button type="button" onClick={() => { void queue.refetch(); void forks.refetch(); }}>Retry queues</button></p> : null}
      {renderEntries(queue.data?.pages.flatMap((page: CurveLibraryPage) => page.items) ?? [], true)}
      {queue.hasNextPage ? <button disabled={queue.isFetching} type="button" onClick={() => void queue.fetchNextPage()}>More submissions</button> : null}
      <h3>Retained customized copies</h3>
      <p>Copy history remains here after publication or review. Pending master-approval submissions are listed separately above.</p>
      <ul>{forks.data?.pages.flatMap((page) => page.items).map((forked) => <li key={forked.id}>
        <span>{forked.reason} · {forked.state} · {forked.created_by} · {forked.requires_library_review ? 'Separate library review required' : 'Current copy independently approved'}</span>{' '}
        <button disabled={disabled || busy} type="button" onClick={() => onOpenImport(forked.child_import_id)}>Open copied draft</button>
      </li>)}</ul>
      {forks.hasNextPage ? <button disabled={forks.isFetching} type="button" onClick={() => void forks.fetchNextPage()}>More copied drafts</button> : null}
    </details> : null}
  </section>;
}
